/**
 * Smart add (PRD §6.5): paste a URL, get a reviewable block proposal.
 *
 * Pure — no React, no network. The Add sheet renders the proposal as a normal
 * catalogue row with an explicit Add/Update button; nothing is created until
 * the employer confirms.
 *
 * Rules:
 * - Google Maps URL → "Where we work" (map). The map block is unique: when one
 *   already exists the proposal updates it instead of adding a second.
 * - Supported video URL → null for Phase 1 (the Video block is next phase).
 * - Any other valid HTTPS URL → a Links block on a company page (the URL
 *   becomes its first row) or a Website block on an individual one.
 * - Anything that is not a valid https:// URL → null.
 */

export interface SmartAddProposal {
  kind: 'new' | 'update-unique';
  type: 'map' | 'website' | 'links';
  config: { url: string };
  /** Set when kind is 'update-unique': the block the proposal would update. */
  existingBlockId?: string;
}

export interface SmartAddExistingBlock {
  id: string;
  type: string;
}

/** True for a syntactically valid absolute https:// URL with a real host. */
export function isValidHttpsUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return false;
  }

  return parsed.protocol === 'https:' && parsed.hostname.includes('.');
}

const GOOGLE_MAPS_SHORTLINK_HOSTS = new Set([
  'maps.app.goo.gl',
  'goo.gl',
  'g.co',
]);

function isGoogleMapsUrl(parsed: URL): boolean {
  const host = parsed.hostname.toLowerCase();
  const path = parsed.pathname.toLowerCase();

  if (host === 'maps.google.com' || host.startsWith('maps.google.')) {
    return true;
  }

  if (GOOGLE_MAPS_SHORTLINK_HOSTS.has(host)) {
    // goo.gl/g.co only count for their /maps shortlinks; maps.app.goo.gl is
    // always a maps destination.
    return host === 'maps.app.goo.gl' || path.startsWith('/maps');
  }

  // google.com/maps/..., www.google.ae/maps/... (any Google ccTLD).
  if (host === 'google.com' || host.startsWith('www.google.') || host.startsWith('google.')) {
    return path === '/maps' || path.startsWith('/maps/');
  }

  return false;
}

const VIDEO_HOSTS = [
  'youtube.com',
  'youtu.be',
  'vimeo.com',
  'loom.com',
];

function isVideoUrl(parsed: URL): boolean {
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
  return VIDEO_HOSTS.some((videoHost) => host === videoHost || host.endsWith(`.${videoHost}`));
}

export function proposeBlockFromUrl(
  url: string,
  {
    existingBlocks,
    mode = 'individual',
  }: { existingBlocks: SmartAddExistingBlock[]; mode?: 'individual' | 'company' },
): SmartAddProposal | null {
  const trimmed = url.trim();
  if (!isValidHttpsUrl(trimmed)) {
    return null;
  }

  const parsed = new URL(trimmed);

  if (isGoogleMapsUrl(parsed)) {
    const existingMap = existingBlocks.find((block) => block.type === 'map');
    if (existingMap) {
      return {
        kind: 'update-unique',
        type: 'map',
        config: { url: trimmed },
        existingBlockId: existingMap.id,
      };
    }
    return { kind: 'new', type: 'map', config: { url: trimmed } };
  }

  // Video URLs propose a Video block only once that block ships (next phase).
  if (isVideoUrl(parsed)) {
    return null;
  }

  // A company has no Website block to drop this into — its own site is a hero
  // link — so a pasted URL starts a Links block instead, with this as row one.
  return {
    kind: 'new',
    type: mode === 'company' ? 'links' : 'website',
    config: { url: trimmed },
  };
}

/**
 * The company Add sheet has ONE combined input: a pasted URL shows a smart-add
 * proposal, a valid-but-unsupported URL (video, for now) shows a hint, and any
 * other text filters the offered blocks. Shared by both platform sheets so the
 * tri-state decision cannot drift between twins.
 */
export type CombinedAddInput =
  | { kind: 'empty' }
  | { kind: 'proposal'; proposal: SmartAddProposal }
  | { kind: 'unsupported-url' }
  | { kind: 'search'; query: string };

export function resolveCombinedAddInput(
  value: string,
  context: {
    existingBlocks: SmartAddExistingBlock[];
    mode?: 'individual' | 'company';
  },
): CombinedAddInput {
  const trimmed = value.trim();
  if (!trimmed) {
    return { kind: 'empty' };
  }

  const proposal = proposeBlockFromUrl(trimmed, context);
  if (proposal) {
    return { kind: 'proposal', proposal };
  }

  if (isValidHttpsUrl(trimmed)) {
    return { kind: 'unsupported-url' };
  }

  return { kind: 'search', query: trimmed };
}
